// Field Labels room and gear photos: taking one, sending it, and showing it small.
// Words: ROOM_GEAR_PHOTO_B5_WORDS.md. Mockup: MOCKUP_ROOM_GEAR_PHOTO.html (approved by Robert 2026-10-29 5:19 AM).
//
// * One photo per room, one per gear. It waits on the iPad in the Outbox and sends on signal, like the label PDFs.
// * It goes to Drive in 00 Field Labels / Photos as 00_ER2.jpg or 00_ER2_XFMR-01.jpg (functions/src/drive.ts).
// * Retake sends a new one under the same name: Drive replaces the file in place.
// * A re-code or a gear rename sends it again under the new name, and tells the server the old one (was),
//   so the Drive file is renamed rather than a second one made.
// * The picture shows from this iPad. A photo another iPad took shows from the cloud when there is signal.

import { outboxPath, photoSendName } from "./drive-folders";
import type { WalkPhoto } from "./field-labels-site";
import { walkPhotoUrl } from "./firebase";
import { isOnline } from "./offline";
import { deviceList } from "./outbox";
import { saveDrivePdf } from "./survey-sync";

/** Small pictures by Outbox path, so a redraw never waits on the iPad's store. */
const thumbs = new Map<string, string>();
const loading = new Set<string>();

/** The picture for a photo, or "" while it loads. */
export function thumbUrl(path: string): string {
  return thumbs.get(path) ?? "";
}

/** Fills every picture of this photo on the page once it has loaded. A redraw before that picks it up from the cache. */
function fill(path: string, url: string): void {
  thumbs.set(path, url);
  document.querySelectorAll<HTMLImageElement>("img[data-thumb]").forEach((img) => {
    if (img.dataset.thumb === path && img.src !== url) img.src = url;
  });
}

/** Not on this iPad and no signal: the full-size sheet says where the photo is. */
function notHere(path: string): void {
  document.querySelectorAll<HTMLElement>("[data-thumb-wait]").forEach((el) => {
    if (el.dataset.thumbWait === path) el.hidden = false;
  });
}

/** Loads the pictures a draw asked for: from this iPad first, else from the cloud. */
export function loadThumbs(root: ParentNode): void {
  root.querySelectorAll<HTMLImageElement>("img[data-thumb]").forEach((img) => {
    const path = img.dataset.thumb ?? "";
    if (!path) return;
    const have = thumbs.get(path);
    if (have) {
      if (img.src !== have) img.src = have;
      return;
    }
    if (loading.has(path)) return;
    loading.add(path);
    void (async () => {
      try {
        const e = await deviceList.photo(path);
        if (e?.blob) return fill(path, URL.createObjectURL(e.blob));
        if (!isOnline()) return notHere(path);
        fill(path, await walkPhotoUrl(path));
      } catch {
        // Not on this iPad and not in the cloud yet: tried again on the next draw.
        notHere(path);
      } finally {
        loading.delete(path);
      }
    })();
  });
}

const LONG_SIDE = 2400;

/** Every photo goes as a JPEG no longer than 2400 px. The iPad camera app can hand back other kinds and sizes. */
export async function toJpeg(blob: Blob): Promise<Blob> {
  if (blob.type === "image/jpeg" && blob.size <= 8 * 1024 * 1024) return blob;
  const url = URL.createObjectURL(blob);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    const scale = Math.min(1, LONG_SIDE / Math.max(img.naturalWidth, img.naturalHeight));
    const c = document.createElement("canvas");
    c.width = Math.round(img.naturalWidth * scale);
    c.height = Math.round(img.naturalHeight * scale);
    c.getContext("2d")?.drawImage(img, 0, 0, c.width, c.height);
    const out = await new Promise<Blob | null>((res) => c.toBlob(res, "image/jpeg", 0.85));
    if (!out) throw new Error("The photo could not be read.");
    return out;
  } finally {
    URL.revokeObjectURL(url);
  }
}

export interface PhotoSend {
  company: string;
  site: string;
  badgeId: string;
  byName: string;
}

/** Puts one send in the Outbox. was: the Drive name to rename, when it moved. */
async function queue(o: PhotoSend, id: string, driveName: string, blob: Blob, was: string): Promise<WalkPhoto> {
  const at = new Date().toISOString();
  const path = outboxPath(o.company, o.site, "photo", photoSendName(driveName, Date.now()));
  await saveDrivePdf({
    path,
    company: o.company,
    siteNumber: o.site,
    assetId: "",
    contentType: "image/jpeg",
    blob,
    takenAt: at,
    meta: { badgeId: o.badgeId, byName: o.byName, ...(was && was !== driveName ? { was } : {}) },
  });
  thumbs.set(path, URL.createObjectURL(blob));
  return { id, at, path, sent: driveName };
}

/** The last send's copy on this iPad. It is gone once a newer send of the same photo holds the picture. */
async function drop(path: string): Promise<void> {
  try {
    await deviceList.deletePhoto(path);
  } catch {
    // Left on the iPad. It never sends twice: it is already sent or the server keeps the newer one.
  }
  const url = thumbs.get(path);
  if (url?.startsWith("blob:")) URL.revokeObjectURL(url);
  thumbs.delete(path);
}

/** A new photo, or a Retake. The record to keep on the room or gear. */
export async function takePhoto(o: PhotoSend, driveName: string, shot: Blob, prev: WalkPhoto | undefined): Promise<WalkPhoto> {
  const id = `ph${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
  const rec = await queue(o, id, driveName, await toJpeg(shot), prev?.sent ?? "");
  if (prev && prev.path !== rec.path) await drop(prev.path);
  return rec;
}

/**
 * A room re-coded or a gear renamed since its photo went: the same picture goes again under the new name.
 * Null when the picture is not on this iPad (another iPad took it). Drive keeps the old name until that iPad sends.
 */
export async function resendPhoto(o: PhotoSend, photo: WalkPhoto, want: string): Promise<WalkPhoto | null> {
  const e = await deviceList.photo(photo.path);
  if (!e?.blob) return null;
  const rec = await queue(o, photo.id, want, e.blob, photo.sent);
  // The picture and the moment it was taken stay the same. Only the name and the send move.
  rec.at = photo.at;
  await drop(photo.path);
  return rec;
}
